// Shared "walk through the project list" helper.
//
// The overview stores the page of projects you clicked from, plus enough of the
// request to fetch the neighbouring page, so the detail page can offer
// Vorige/Volgend project across the whole filtered list without going back.
//
// Only one page of rows is held at a time. When the open project sits on the
// first or last row of that page, `adjacentPage` names the page the caller
// should fetch to resolve the neighbour that falls off the edge.
const STORAGE_KEY = "project-nav-list";
/**
 * Row identity, matching the grid's own getRowId so the remembered list and the
 * clicked project always resolve to the same key.
 */
export const getSnapshotRowId = (row, fallbackIndex) => {
    const keyParts = [
        row.parentProjectId,
        row.parentProjectNumber,
        row.childProjectNumber,
        row.topParentProjectNumber,
        row.hierarchyLevel,
    ].filter((value) => value !== undefined && value !== null && value !== "");
    if (keyParts.length > 0)
        return keyParts.join("::");
    return fallbackIndex !== undefined ? `snapshot-${fallbackIndex}` : "snapshot";
};
const projectIdOf = (row) => String(row.parentProjectId || getSnapshotRowId(row));
/** Builds the detail-page URL for one overview row. */
export const buildProjectDetailUrl = (row, projectId, companyId, aanneemsom) => {
    const params = new URLSearchParams({
        topProjectNumber: row.parentProjectNumber || "",
        entityName: row.entityName || "",
        companyId,
    });
    if (aanneemsom && aanneemsom.trim() !== "") {
        params.append("aanneemsom", aanneemsom);
    }
    params.append("isChildProject", String(row.isChildProject));
    return `/project/${projectId}/details?${params.toString()}`;
};
/** Turns one overview row into a navigation entry. */
export const toProjectNavEntry = (row, companyId, aanneemsom) => {
    const projectId = projectIdOf(row);
    return {
        projectId,
        topProjectNumber: row.parentProjectNumber || "",
        label: row.parentProjectDescription || row.parentProjectNumber || "",
        url: buildProjectDetailUrl(row, projectId, companyId, aanneemsom),
    };
};
/** Remembers the rows currently on screen, in display order. */
export const rememberProjectNavList = (nav) => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(nav));
    }
    catch (_a) {
        // Private mode / quota: navigation buttons just stay disabled.
    }
};
const readNav = () => {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed === null || parsed === void 0 ? void 0 : parsed.entries) ? parsed : null;
    }
    catch (_a) {
        return null;
    }
};
const NO_NEIGHBOURS = {
    current: null,
    previous: null,
    next: null,
    position: 0,
    total: 0,
    adjacentPage: null,
    adjacentSide: null,
    adjacentRequest: null,
    companyId: "",
};
/**
 * Neighbours of the open project within the remembered list.
 *
 * Identity is the (projectId, topProjectNumber) pair, not projectId alone: a
 * search expands matching child rows into the list, and the backend gives every
 * one of them their parent's projectId. Matching on projectId alone resolves
 * each of those rows to the first one, so the position froze and the chevrons
 * bounced between the same two rows. Falls back to projectId when the caller has
 * no project number, or when the stored list predates the field.
 */
export const getProjectNeighbours = (projectId, topProjectNumber) => {
    const nav = readNav();
    if (!nav)
        return NO_NEIGHBOURS;
    const findBy = (predicate) => projectId ? nav.entries.findIndex(predicate) : -1;
    let index = topProjectNumber
        ? findBy((entry) => entry.projectId === projectId &&
            entry.topProjectNumber === topProjectNumber)
        : -1;
    if (index === -1) {
        index = findBy((entry) => entry.projectId === projectId);
    }
    if (index === -1) {
        return Object.assign(Object.assign({}, NO_NEIGHBOURS), { total: nav.totalCount });
    }
    const page = Math.max(1, nav.page);
    const position = (page - 1) * nav.pageSize + index + 1;
    const previous = index > 0 ? nav.entries[index - 1] : null;
    const next = index < nav.entries.length - 1 ? nav.entries[index + 1] : null;
    // Only one edge can need a fetch at a time: the user is either on the first
    // row or the last row of the page, never both (a single-row page aside, where
    // going forward is the more useful direction).
    let adjacentPage = null;
    let adjacentSide = null;
    if (!next && position < nav.totalCount) {
        adjacentPage = page + 1;
        adjacentSide = "next";
    }
    else if (!previous && position > 1) {
        adjacentPage = page - 1;
        adjacentSide = "previous";
    }
    const adjacentRequest = adjacentPage !== null && nav.request
        ? Object.assign(Object.assign({}, nav.request), { pagination: { page: adjacentPage, pageSize: nav.pageSize } }) : null;
    return {
        current: nav.entries[index],
        previous,
        next,
        position,
        total: nav.totalCount,
        adjacentPage,
        adjacentSide,
        adjacentRequest,
        companyId: nav.companyId,
        aanneemsom: nav.aanneemsom,
    };
};
